import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { API_BASE_URL } from '../services/api';
import BrandLogo from './BrandLogo';

type Status = 'idle' | 'working' | 'done' | 'failed';

// Which email the link came from (?kind=). Absent means the weekly digest, which predates the parameter.
const KINDS = {
  digest: {
    title: 'Weekly digest',
    endpoint: 'digest',
    ask: 'Stop the weekly "what you missed" summary email? Your in-app notifications aren\'t affected.',
    done: 'You\'re unsubscribed — no more weekly digest emails. You can turn it back on any time in your notification settings.',
    manual: 'turn the digest off',
  },
  comeback: {
    title: 'Come-back reminders',
    endpoint: 'comeback',
    ask: 'Stop the "it\'s been a while" reminder emails? Your in-app notifications aren\'t affected.',
    done: 'You\'re unsubscribed — no more come-back reminders. You can turn them back on any time in your notification settings.',
    manual: 'turn these reminders off',
  },
  reminders: {
    title: 'Event reminders',
    endpoint: 'event-reminders',
    ask: 'Stop the day-before reminder emails for events you\'re going to? You\'ll still see them in the app.',
    done: 'You\'re unsubscribed — no more event reminder emails. You can turn them back on any time in your notification settings.',
    manual: 'turn event reminders off',
  },
  tips: {
    title: 'Getting-started tips',
    endpoint: 'getting-started',
    ask: 'Stop getting-started tip emails? Your in-app notifications aren\'t affected.',
    done: 'You\'re unsubscribed — no more getting-started emails. You can turn them back on any time in your notification settings.',
    manual: 'turn these tips off',
  },
  events: {
    title: 'Community event emails',
    endpoint: 'event-suggestions',
    ask: 'Stop the "events coming up in your communities" emails? Your in-app notifications and reminders for events you\'re going to aren\'t affected.',
    done: 'You\'re unsubscribed — no more community event emails. You can turn them back on any time in your notification settings.',
    manual: 'turn these emails off',
  },
} as const;

/**
 * Landing page for the one-click unsubscribe links in the weekly digest, come-back, community event, event reminder and getting-started emails. Works signed out — the signed token in the
 * link is the authorization. It asks for one click rather than acting on load, because mail
 * scanners and link previewers open links without a person ever reading the page.
 */
const Unsubscribe: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const kindParam = searchParams.get('kind');
  const kind = kindParam === 'comeback' ? KINDS.comeback
    : kindParam === 'events' ? KINDS.events
    : kindParam === 'reminders' ? KINDS.reminders
    : kindParam === 'tips' ? KINDS.tips
    : KINDS.digest;
  const [status, setStatus] = useState<Status>('idle');

  const handleUnsubscribe = async () => {
    setStatus('working');
    try {
      const res = await fetch(`${API_BASE_URL}/unsubscribe/${kind.endpoint}?token=${encodeURIComponent(token)}`, { method: 'POST' });
      setStatus(res.ok ? 'done' : 'failed');
    } catch {
      setStatus('failed');
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="flex-start" sx={{ padding: 2 }}>
      <Paper elevation={6} sx={{ padding: { xs: 3, sm: 4 }, maxWidth: 450, width: '100%', borderRadius: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <BrandLogo size={70} stacked textColor="#08263A" accentColor="#0EAFA0" />
        </Box>
        <Typography variant="h6" align="center" sx={{ fontWeight: 700, mb: 1 }}>
          {kind.title}
        </Typography>

        {!token && (
          <Alert severity="error" sx={{ mt: 1 }}>This unsubscribe link is incomplete. You can {kind.manual} any time in your notification settings.</Alert>
        )}

        {token && status !== 'done' && (
          <>
            <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 2 }}>
              {kind.ask}
            </Typography>
            {status === 'failed' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                That didn't work — the link may be invalid. You can {kind.manual} in your notification settings instead.
              </Alert>
            )}
            <Button fullWidth variant="contained" disabled={status === 'working'} onClick={handleUnsubscribe}>
              {status === 'working' ? <CircularProgress size={22} /> : 'Unsubscribe'}
            </Button>
          </>
        )}

        {status === 'done' && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {kind.done}
          </Alert>
        )}

        <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/settings')}>
          Notification settings
        </Button>
      </Paper>
    </Box>
  );
};

export default Unsubscribe;
